import React, {useMemo} from 'react';
import {useTranslation} from 'next-i18next';
import {
	Box,
	Button,
	ButtonGroup,
	scrollBarStyling,
	Stack,
	Text,
} from '@tempo/core';

type RangeSelectorButtonProps = {
	range: RangeSelector;
	text: string;
	onClick: (range: RangeSelector) => void;
	isActive: boolean;
};

const RangeSelectorButton = ({
	range,
	text,
	onClick,
	isActive,
}: RangeSelectorButtonProps) => {
	const handleClick = () => {
		onClick(range);
	};

	return (
		<Button
			variant={isActive ? 'contain' : 'outline'}
			size="small"
			onClick={handleClick}
			width="min-content"
			aria-pressed={isActive}
			data-testid={range.id ? `streams-range-${range.id}` : undefined}
		>
			{text}
		</Button>
	);
};

export type RangeSelector = {
	id?: string;
	text: string;
	startDate: Date;
	endDate: Date;
};

type RangeSelectorsProps = {
	activeRange: RangeSelector;
	rangeButtons: RangeSelector[];
	onRangeChange: (range: RangeSelector) => void;
};

const RangeSelectors = ({
	rangeButtons,
	activeRange,
	onRangeChange,
}: RangeSelectorsProps) => {
	const {t} = useTranslation('d4c');
	const activeButtonIndex = useMemo(() => {
		return rangeButtons.findIndex(
			r =>
				activeRange.startDate === r.startDate &&
				activeRange.endDate === r.endDate,
		);
	}, [activeRange, rangeButtons]);

	return (
		<Stack
			direction={{base: 'column', md: 'row'}}
			mb={4}
			spacing={{base: 'spacing.m', md: 'spacing.xl'}}
			alignItems={{base: 'flex-start', md: 'center'}}
		>
			<Text>{t('analyticshome_text_jumptothelast_d4c-web')}</Text>
			<Box
				overflow="auto"
				maxWidth="100%"
				sx={scrollBarStyling}
				alignSelf={{base: 'center', md: 'initial'}}
			>
				<ButtonGroup isAttached width="fit-content" size="small">
					{rangeButtons.map((b, index) => (
						<RangeSelectorButton
							key={`${b.text}-${index}`}
							onClick={onRangeChange}
							range={b}
							text={b.text}
							isActive={index === activeButtonIndex}
						/>
					))}
				</ButtonGroup>
			</Box>
		</Stack>
	);
};

export default RangeSelectors;
